import React, { useState, useEffect, useRef } from 'react';
import {
  GetColor,
  formatPowerAutoUnit,
  formatFlowAutoUnit,
  formatEnergyAutoUnit,
} from '../../../../utils';
import { getTopGeneration } from '../../../../services';
import * as s from './styled-pracas-mapa';

// Dock "TOP GERACAO" no telao (so tela cheia): ranking das maquinas (UCHA)
// que mais geram agora, do endpoint /thing/top-generation (escopado por
// tenant, cache 15s no back). Substitui a legenda solta — a chave de cores
// vira um rodape compacto aqui dentro (sem perder a referencia).

const POLL_MS = 30 * 1000;
const LIMIT = 10;

// Itens da legenda de status (mesma do MapLegend) — versao compacta.
const LEGEND_ITEMS = [
  { name: 'alarme/bloqueio', label: 'Alarme' },
  { name: 'atenção', label: 'Atenção' },
  { name: 'rodando', label: 'Rodando' },
  { name: 'desligada', label: 'Desligada' },
  { name: 'sem dados', label: 'Sem dados' },
];

const FullscreenTopGen = ({ activeFilters, onToggle }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Toggle Geracao (UCHA) / Consumo (EBA) — mesmo endpoint, param mode.
  const [mode, setMode] = useState('generation');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setRows([]);
    setLoaded(false);
    const fetchTop = () => {
      getTopGeneration(LIMIT, mode)
        .then((res) => {
          if (!mountedRef.current) return;
          // A resposta pode vir como [] direto, { data: [] } (1 wrap) ou
          // { data: { data: [] } } (o endpoint embrulha + BaseController.ok
          // embrulha de novo). Desce ate achar o array.
          const body = (res && res.data) || res || [];
          const arr = Array.isArray(body)
            ? body
            : body && Array.isArray(body.data)
            ? body.data
            : [];
          setRows(arr);
          setLoaded(true);
        })
        .catch(() => {
          if (mountedRef.current) setLoaded(true);
        });
    };
    fetchTop();
    const id = setInterval(fetchTop, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [mode]);

  const maxGen = rows.reduce(
    (m, r) => Math.max(m, Number(r.generationKw) || 0),
    0,
  );

  return (
    <>
      <s.TopGenHeader>
        <span className="dot" aria-hidden="true" />
        <s.TopGenModeToggle role="tablist" aria-label="Modo do ranking">
          <s.TopGenModeBtn
            type="button"
            role="tab"
            aria-selected={mode === 'generation'}
            $active={mode === 'generation'}
            $mode="generation"
            onClick={() => setMode('generation')}
          >
            TGA
          </s.TopGenModeBtn>
          <s.TopGenModeBtn
            type="button"
            role="tab"
            aria-selected={mode === 'consumption'}
            $active={mode === 'consumption'}
            $mode="consumption"
            onClick={() => setMode('consumption')}
          >
            Bombas
          </s.TopGenModeBtn>
        </s.TopGenModeToggle>
        <span className="meta">tempo real</span>
      </s.TopGenHeader>

      <s.TopGenList>
        {loaded && rows.length === 0 ? (
          <s.TopGenEmpty>
            {mode === 'generation'
              ? 'Nenhuma TGA gerando agora.'
              : 'Nenhuma bomba operando agora.'}
          </s.TopGenEmpty>
        ) : (
          rows.map((r, idx) => {
            const gen = Number(r.generationKw) || 0;
            const pct = maxGen > 0 ? (gen / maxGen) * 100 : 0;
            const sub = [r.parkName, r.customerName].filter(Boolean).join(' · ');
            return (
              <s.TopGenCard key={r.thingId || `${idx}-${r.thingName}`}>
                <s.TopGenRank $top={idx === 0}>{idx + 1}</s.TopGenRank>
                <s.TopGenBody>
                  <s.TopGenName title={r.thingName}>
                    {r.thingName || `Máquina ${idx + 1}`}
                  </s.TopGenName>
                  <s.TopGenMetaRow>
                    {r.model && (
                      <s.TopGenModel title={`Modelo: ${r.model}`}>
                        {r.model}
                      </s.TopGenModel>
                    )}
                    {sub && <s.TopGenSub title={sub}>{sub}</s.TopGenSub>}
                  </s.TopGenMetaRow>
                  <s.TopGenBarTrack>
                    <s.TopGenBarFill
                      $mode={mode}
                      style={{ width: `${pct}%` }}
                    />
                  </s.TopGenBarTrack>
                  {/* Total acumulado da maquina (gerado/consumido). So
                      quando o backend retorna o valor. */}
                  {r.totalEnergyKwh != null &&
                    Number.isFinite(Number(r.totalEnergyKwh)) && (
                      <s.TopGenTotal title="Energia acumulada total da máquina">
                        {mode === 'consumption'
                          ? 'Total consumido'
                          : 'Total gerado'}
                        :{' '}
                        <b>
                          {formatEnergyAutoUnit(
                            Number(r.totalEnergyKwh),
                            'kWh',
                          )}
                        </b>
                      </s.TopGenTotal>
                    )}
                </s.TopGenBody>
                {/* Bombas: vazao em destaque + potencia atual em baixo
                    (Vazao / Potencia). TGA: so a potencia gerada. */}
                {mode === 'consumption' ? (
                  <s.TopGenValue title="Vazão atual / potência atual">
                    {r.flowLps != null
                      ? formatFlowAutoUnit(Number(r.flowLps), 'l/s')
                      : '—'}
                    <small>{formatPowerAutoUnit(gen, 'kW')}</small>
                  </s.TopGenValue>
                ) : (
                  <s.TopGenValue>{formatPowerAutoUnit(gen, 'kW')}</s.TopGenValue>
                )}
              </s.TopGenCard>
            );
          })
        )}
      </s.TopGenList>

      {/* Legenda de cores compacta — preserva a chave dos markers que o
          dock substituiu. Interativa: clica pra filtrar status. */}
      <s.TopGenLegend>
        {LEGEND_ITEMS.map(({ name, label }) => {
          const isActive = (activeFilters || []).includes(name);
          return (
            <s.TopGenLegendItem
              key={name}
              type="button"
              $active={isActive}
              onClick={() => onToggle && onToggle(name)}
              title={isActive ? `Esconder ${label}` : `Mostrar ${label}`}
            >
              <s.TopGenLegendDot color={GetColor(name)} $hollow={!isActive} />
              {label}
            </s.TopGenLegendItem>
          );
        })}
      </s.TopGenLegend>
    </>
  );
};

export default FullscreenTopGen;
